import React, { useMemo } from 'react';
import { CelestialNode } from './CelestialMoonChallenge';
import { GiWolfHowl, GiScorpion, GiBull, GiPolarBear, GiLion, GiQueenCrown, GiPegasus, GiArcher, GiSwan, GiLyre } from 'react-icons/gi';

interface CelestialLevelArtworkProps {
  nodes: CelestialNode[];
  currentStep: number;
  moonBrightness: number;
  containerWidth?: number;
}

// Deterministic hash function for uniform, non-aligned star field
function pseudoRandom(seed: number) {
  let t = (seed + 0x6D2B79F5) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

export const CelestialLevelArtwork: React.FC<CelestialLevelArtworkProps> = React.memo(({
  nodes,
  currentStep,
  moonBrightness,
  containerWidth
}) => {
  const w = Math.max(400, containerWidth || 400);
  const viewBoxLeft = Math.round(200 - w / 2);
  const viewBoxWidth = w;

  // Calculate dynamic outward spreading for wide desktop/tablet screens
  const extraWidth = Math.max(0, viewBoxWidth - 400);
  const spread = extraWidth * 0.38;
  const scaleBoost = Math.min(1.35, 1 + (extraWidth / 1200) * 0.22);

  const cGrandChien = { x: Math.round(85 - spread), y: 4100, scale: Number((1.25 * scaleBoost).toFixed(2)) };
  const cScorpion = { x: Math.round(245 + spread + 10), y: 3760, scale: Number((1.15 * scaleBoost).toFixed(2)) };
  const cTaureau = { x: Math.round(100 - spread), y: 3470, scale: Number((1.2 * scaleBoost).toFixed(2)) };
  const cGrandeOurse = { x: Math.round(75 - spread - 15), y: 3120, scale: Number((1.15 * scaleBoost).toFixed(2)) };
  const cLion = { x: Math.round(240 + spread), y: 2770, scale: Number((1.15 * scaleBoost).toFixed(2)) };
  const cCassiopee = { x: Math.round(90 - spread), y: 2380, scale: Number((1.2 * scaleBoost).toFixed(2)) };
  const cPegase = { x: Math.round(235 + spread), y: 1970, scale: Number((1.15 * scaleBoost).toFixed(2)) };
  const cOrion = { x: Math.round(85 - spread), y: 1570, scale: Number((1.2 * scaleBoost).toFixed(2)) };
  const cPetiteOurse = { x: Math.round(240 + spread), y: 1170, scale: Number((1.2 * scaleBoost).toFixed(2)) };
  const cCygne = { x: Math.round(100 - spread), y: 810, scale: Number((1.2 * scaleBoost).toFixed(2)) };
  const cLyre = { x: Math.round(235 + spread), y: 470, scale: Number((1.25 * scaleBoost).toFixed(2)) };

  // Pre-generate organic, non-aligned background stars using hash PRNG
  const backgroundStars = useMemo(() => {
    const totalStars = 1100;
    const colors = [
      '#ffffff', '#ffffff', '#ffffff', '#e2e8f0',
      '#fde047', '#38bdf8', '#fb923c', '#34d399', '#c084fc'
    ];
    const stars = [];
    for (let i = 0; i < totalStars; i++) {
      const u = pseudoRandom(i * 17 + 3);
      const v = pseudoRandom(i * 29 + 11);
      const randR = pseudoRandom(i * 43 + 7);
      const randCol = pseudoRandom(i * 59 + 19);
      const randOp = pseudoRandom(i * 71 + 31);
      
      const r = randR > 0.9 ? 1.6 : randR > 0.55 ? 1.1 : 0.7;
      const color = colors[Math.floor(randCol * colors.length)];
      const baseOpacity = 0.25 + randOp * 0.55;
      const isTwinkling = (i % 3 === 0);
      const dur = isTwinkling ? (3 + (i % 4) * 1.5) + 's' : undefined;

      stars.push({
        id: i,
        u, // 0 to 1 horizontal fraction
        cy: Math.round(v * 5520 + 20),
        r,
        color,
        baseOpacity,
        isTwinkling,
        dur
      });
    }
    return stars;
  }, []);

  // Build segments instead of one path to allow coloring different constellations
  const segments = useMemo(() => {
    if (!nodes || nodes.length < 2) return [];
    const segs = [];
    for (let i = 1; i < nodes.length; i++) {
      segs.push({
        x1: nodes[i-1].x,
        y1: nodes[i-1].y,
        x2: nodes[i].x,
        y2: nodes[i].y,
        color: nodes[i].color || '#38bdf8',
        isPassed: i <= currentStep,
      });
    }
    return segs;
  }, [nodes, currentStep]);

  return (
    <svg
      className="absolute top-0 left-0 w-full h-full pointer-events-none z-0"
      viewBox={`${viewBoxLeft} 0 ${viewBoxWidth} 5600`}
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* ========================================================= */}
        {/* ATMOSPHERE & GLOW FILTERS                                 */}
        {/* ========================================================= */}
        <linearGradient id="celestialDeepCosmos" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#010309" />
          <stop offset="15%" stopColor="#030712" />
          <stop offset="35%" stopColor="#080e22" />
          <stop offset="55%" stopColor="#0f172a" />
          <stop offset="75%" stopColor="#141432" />
          <stop offset="90%" stopColor="#0a1226" />
          <stop offset="100%" stopColor="#020617" />
        </linearGradient>


        <radialGradient id="moonGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fef08a" stopOpacity="0.85" />
          <stop offset="35%" stopColor="#fde047" stopOpacity="0.45" />
          <stop offset="70%" stopColor="#38bdf8" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#020617" stopOpacity="0" />
        </radialGradient>

        <linearGradient id="cometGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#38bdf8" stopOpacity="0" />
          <stop offset="80%" stopColor="#38bdf8" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
        </linearGradient>

        <linearGradient id="magicFire" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.9" />
          <stop offset="50%" stopColor="#34d399" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#a7f3d0" stopOpacity="0" />
        </linearGradient>

        {/* Minimalist Star Symbol definition */}
        <g id="starSymbol">
          <polygon points="0,-7 2,-2 7,0 2,2 0,7 -2,2 -7,0 -2,-2" fill="#38bdf8" />
          <circle cx="0" cy="0" r="1.5" fill="#ffffff" />
        </g>
      </defs>

      {/* ========================================================= */}
      {/* 1. ÉPURÉ DEEP COSMOS VOID & DECORATIONS                   */}
      {/* ========================================================= */}
      <rect x={viewBoxLeft - 300} y="0" width={viewBoxWidth + 600} height="5600" fill="url(#celestialDeepCosmos)" />

      {/* Lateral Cosmic Nebulae for wide screens */}
      {viewBoxWidth > 500 && (
        <g opacity="0.45" pointerEvents="none">
          <circle cx={cGrandChien.x} cy="4100" r="280" fill="url(#moonGlow)" opacity="0.10" />
          <circle cx={cScorpion.x} cy="3760" r="320" fill="url(#moonGlow)" opacity="0.12" />
          <circle cx={cGrandeOurse.x} cy="3120" r="280" fill="url(#moonGlow)" opacity="0.10" />
          <circle cx={cLion.x} cy="2770" r="290" fill="url(#moonGlow)" opacity="0.11" />
          <circle cx={cCassiopee.x} cy="2380" r="300" fill="url(#moonGlow)" opacity="0.12" />
          <circle cx={cPegase.x} cy="1970" r="300" fill="url(#moonGlow)" opacity="0.10" />
          <circle cx={cOrion.x} cy="1570" r="310" fill="url(#moonGlow)" opacity="0.12" />
          <circle cx={cCygne.x} cy="810" r="280" fill="url(#moonGlow)" opacity="0.12" />
          {/* Lower nebulae expanding into the bottom screen area */}
          <circle cx={viewBoxLeft + 80} cy="4700" r="340" fill="url(#moonGlow)" opacity="0.09" />
          <circle cx={viewBoxLeft + viewBoxWidth - 80} cy="5100" r="360" fill="url(#moonGlow)" opacity="0.08" />
        </g>
      )}
      
      {/* Etoiles Filantes (Comets) - Realistic */}
      <g opacity="0.8">
        <g transform="translate(100, 4000) rotate(135)">
          <line x1="0" y1="0" x2="80" y2="0" stroke="url(#cometGrad)" strokeWidth="1.5" strokeLinecap="round" className="animate-comet-1" />
        </g>
        <g transform="translate(350, 3000) rotate(135)">
          <line x1="0" y1="0" x2="120" y2="0" stroke="url(#cometGrad)" strokeWidth="2" strokeLinecap="round" className="animate-comet-2" />
        </g>
        <g transform="translate(200, 1500) rotate(120)">
          <line x1="0" y1="0" x2="60" y2="0" stroke="url(#cometGrad)" strokeWidth="1.5" strokeLinecap="round" className="animate-comet-3" />
        </g>
        <g transform="translate(380, 800) rotate(145)">
          <line x1="0" y1="0" x2="100" y2="0" stroke="url(#cometGrad)" strokeWidth="2" strokeLinecap="round" className="animate-comet-4" />
        </g>
        <g transform="translate(120, 4900) rotate(135)">
          <line x1="0" y1="0" x2="90" y2="0" stroke="url(#cometGrad)" strokeWidth="1.6" strokeLinecap="round" className="animate-comet-2" />
        </g>
        {viewBoxWidth > 600 && (
          <>
            <g transform={`translate(${cGrandChien.x - 30}, 4200) rotate(135)`}>
              <line x1="0" y1="0" x2="90" y2="0" stroke="url(#cometGrad)" strokeWidth="1.5" strokeLinecap="round" className="animate-comet-1" />
            </g>
            <g transform={`translate(${cTaureau.x - 40}, 3420) rotate(130)`}>
              <line x1="0" y1="0" x2="85" y2="0" stroke="url(#cometGrad)" strokeWidth="1.5" strokeLinecap="round" className="animate-comet-2" />
            </g>
            <g transform={`translate(${cLion.x + 35}, 2720) rotate(140)`}>
              <line x1="0" y1="0" x2="105" y2="0" stroke="url(#cometGrad)" strokeWidth="2" strokeLinecap="round" className="animate-comet-3" />
            </g>
            <g transform={`translate(${cCassiopee.x - 30}, 2250) rotate(130)`}>
              <line x1="0" y1="0" x2="90" y2="0" stroke="url(#cometGrad)" strokeWidth="1.5" strokeLinecap="round" className="animate-comet-2" />
            </g>
            <g transform={`translate(${cPegase.x + 40}, 1880) rotate(140)`}>
              <line x1="0" y1="0" x2="100" y2="0" stroke="url(#cometGrad)" strokeWidth="1.8" strokeLinecap="round" className="animate-comet-4" />
            </g>
            <g transform={`translate(${cPetiteOurse.x + 35}, 1120) rotate(140)`}>
              <line x1="0" y1="0" x2="110" y2="0" stroke="url(#cometGrad)" strokeWidth="2" strokeLinecap="round" className="animate-comet-3" />
            </g>
          </>
        )}
      </g>

      {/* Vraies Constellations en Arrière-plan (Positionnées dynamiquement sur les ailes latérales) */}
      <g>
        
        {/* 1. GRAND CHIEN (Canis Major) */}
        <g transform={`translate(${cGrandChien.x}, ${cGrandChien.y}) scale(${cGrandChien.scale})`}>
          <g transform="translate(-40, -50)">
             <GiWolfHowl size={100} color="rgba(191, 219, 254, 0.15)" />
          </g>
          {/* Lignes d'astérisme de la constellation */}
          <path d="M 0,0 L -18,22 L -8,46 L 16,34 L 10,12 L 34,-12 L 0,0 M 0,0 L 14,-40" fill="none" stroke="rgba(254, 240, 138, 0.7)" strokeWidth="1.2" strokeDasharray="3 3" />
          {/* Sirius (Étoile la plus brillante du ciel nocturne) */}
          <circle cx="0" cy="0" r="8" fill="rgba(191, 219, 254, 0.3)" />
          <circle cx="0" cy="0" r="3.5" fill="#ffffff" />
          <polygon points="0,-9 2,-2 9,0 2,2 0,9 -2,2 -9,0 -2,-2" fill="#bfdbfe" />
          {/* Autres étoiles principales */}
          <circle cx="34" cy="-12" r="2" fill="#ffffff" />
          <circle cx="14" cy="-40" r="2" fill="#ffffff" />
          <circle cx="10" cy="12" r="1.8" fill="#ffffff" />
          <circle cx="-18" cy="22" r="2.2" fill="#ffffff" />
          <circle cx="-8" cy="46" r="2.2" fill="#ffffff" />
          <circle cx="16" cy="34" r="2" fill="#ffffff" />
          
          <text x="5" y="-85" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ GRAND CHIEN ✦</text>
        </g>

        {/* 2. LE SCORPION (Scorpius) */}
        <g transform={`translate(${cScorpion.x}, ${cScorpion.y}) scale(${cScorpion.scale})`}>
          <g transform="translate(-80, -50)">
             <GiScorpion size={140} color="rgba(252, 165, 165, 0.15)" />
          </g>
          {/* Lignes d'astérisme */}
          <path d="M 45,-42 L 20,-25 L 0,0 L -8,22 L -18,45 L -35,68 L -58,74 L -76,58 L -72,38 L -55,34 M -42,-36 L 0,0" fill="none" stroke="rgba(254, 240, 138, 0.7)" strokeWidth="1.2" strokeDasharray="3 3" />
          {/* Antares */}
          <circle cx="0" cy="0" r="10" fill="rgba(239, 68, 68, 0.25)" />
          <circle cx="0" cy="0" r="3.5" fill="#f87171" />
          <polygon points="0,-8 2,-2 8,0 2,2 0,8 -2,2 -8,0 -2,-2" fill="#fecaca" />
          {/* Étoiles du Scorpion */}
          <circle cx="45" cy="-42" r="2.2" fill="#ffffff" />
          <circle cx="20" cy="-25" r="2" fill="#ffffff" />
          <circle cx="-42" cy="-36" r="2" fill="#ffffff" />
          <circle cx="-8" cy="22" r="1.8" fill="#ffffff" />
          <circle cx="-18" cy="45" r="2" fill="#ffffff" />
          <circle cx="-35" cy="68" r="2.2" fill="#ffffff" />
          <circle cx="-58" cy="74" r="2.2" fill="#ffffff" />
          <circle cx="-76" cy="58" r="2" fill="#ffffff" />
          <circle cx="-55" cy="34" r="2.5" fill="#ffffff" />
          
          <text x="-15" y="-85" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ LE SCORPION ✦</text>
        </g>

        {/* 3. LE TAUREAU (Taurus & Pléiades) */}
        <g transform={`translate(${cTaureau.x}, ${cTaureau.y}) scale(${cTaureau.scale})`}>
          <g transform="translate(-55, -90)">
             <GiBull size={130} color="rgba(253, 230, 138, 0.15)" />
          </g>
          {/* Lignes d'astérisme du Taureau */}
          <path d="M -52,-84 L -15,-35 L 0,0 L 16,36 M 74,-84 L 34,-35 L 0,0 M 0,0 L 62,10" fill="none" stroke="rgba(254, 240, 138, 0.7)" strokeWidth="1.2" strokeDasharray="3 3" />
          {/* Aldébaran */}
          <circle cx="0" cy="0" r="9" fill="rgba(249, 115, 22, 0.3)" />
          <circle cx="0" cy="0" r="3.5" fill="#fb923c" />
          <polygon points="0,-8 2,-2 8,0 2,2 0,8 -2,2 -8,0 -2,-2" fill="#fed7aa" />
          {/* Étoiles du Taureau */}
          <circle cx="-52" cy="-84" r="2.2" fill="#ffffff" />
          <circle cx="74" cy="-84" r="2.2" fill="#ffffff" />
          <circle cx="-15" cy="-35" r="1.8" fill="#ffffff" />
          <circle cx="34" cy="-35" r="1.8" fill="#ffffff" />
          
          <text x="10" y="-105" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ LE TAUREAU ✦</text>
        </g>

        {/* 4. LA GRANDE OURSE (Ursa Major) */}
        <g transform={`translate(${cGrandeOurse.x}, ${cGrandeOurse.y}) scale(${cGrandeOurse.scale})`}>
          <g transform="translate(-30, -60)">
             <GiPolarBear size={160} color="rgba(248, 250, 252, 0.15)" />
          </g>
          {/* Casserole & Lignes de l'Ourse */}
          <path d="M -42,2 L -20,8 L 0,0 L 25,18 L 65,10 L 85,38 L 50,48 L 25,18 M 65,10 L 100,10 L 138,-4" fill="none" stroke="rgba(254, 240, 138, 0.75)" strokeWidth="1.3" strokeDasharray="3 3" />
          {/* Étoiles principales */}
          <circle cx="-42" cy="2" r="2.5" fill="#ffffff" />
          <circle cx="-20" cy="8" r="2.2" fill="#ffffff" />
          <circle cx="0" cy="0" r="2.2" fill="#ffffff" />
          <circle cx="25" cy="18" r="2" fill="#ffffff" />
          <circle cx="65" cy="10" r="2.8" fill="#fde047" />
          <circle cx="85" cy="38" r="2.5" fill="#ffffff" />
          <circle cx="50" cy="48" r="2.2" fill="#ffffff" />
          <circle cx="138" cy="-4" r="1.8" fill="#bfdbfe" />
          
          <text x="45" y="-85" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ GRANDE OURSE ✦</text>
        </g>

        {/* 5. LE LION (Leo) */}
        <g transform={`translate(${cLion.x}, ${cLion.y}) scale(${cLion.scale})`}>
          <g transform="translate(-45, -80)">
             <GiLion size={130} color="rgba(253, 224, 71, 0.15)" />
          </g>
          {/* Lignes d'astérisme du Lion */}
          <path d="M 0,0 L 4,-36 L -18,-52 L -36,-44 L -44,-24 L -18,-2 M 0,0 L 45,-12 L 88,-4 L 60,18 L 25,14 L 0,0" fill="none" stroke="rgba(254, 240, 138, 0.7)" strokeWidth="1.2" strokeDasharray="3 3" />
          {/* Régulus */}
          <circle cx="0" cy="0" r="9" fill="rgba(191, 219, 254, 0.3)" />
          <circle cx="0" cy="0" r="3.5" fill="#ffffff" />
          <polygon points="0,-8 2,-2 8,0 2,2 0,8 -2,2 -8,0 -2,-2" fill="#bfdbfe" />
          {/* Étoiles du Lion */}
          <circle cx="4" cy="-36" r="2.2" fill="#fde047" />
          <circle cx="-18" cy="-52" r="2" fill="#ffffff" />
          <circle cx="-36" cy="-44" r="1.8" fill="#ffffff" />
          <circle cx="45" cy="-12" r="2" fill="#ffffff" />
          <circle cx="88" cy="-4" r="2.5" fill="#ffffff" />
          <circle cx="60" cy="18" r="2" fill="#ffffff" />
          
          <text x="25" y="-85" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ LE LION ✦</text>
        </g>

        {/* 6. CASSIOPÉE (Cassiopeia) */}
        <g transform={`translate(${cCassiopee.x}, ${cCassiopee.y}) scale(${cCassiopee.scale})`}>
          <g transform="translate(-15, -60)">
             <GiQueenCrown size={120} color="rgba(252, 165, 165, 0.15)" />
          </g>
          {/* Trône royal sculpté suivant le W */}
          <path d="M -10,-25 L 15,15 L 45,-8 L 75,25 L 105,0" fill="none" stroke="rgba(254, 240, 138, 0.7)" strokeWidth="1.5" strokeDasharray="3 3" />
          <circle cx="-10" cy="-25" r="2.2" fill="#ffffff" />
          <circle cx="15" cy="15" r="2.4" fill="#ffffff" />
          <circle cx="45" cy="-8" r="3" fill="#bfdbfe" />
          <circle cx="75" cy="25" r="2.8" fill="#fca5a5" />
          <circle cx="105" cy="0" r="2.5" fill="#ffffff" />
          
          <text x="45" y="-85" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ CASSIOPÉE ✦</text>
        </g>

        {/* 7. PÉGASE (Pegasus) */}
        <g transform={`translate(${cPegase.x}, ${cPegase.y}) scale(${cPegase.scale})`}>
          <g transform="translate(-65, -50)">
             <GiPegasus size={120} color="rgba(248, 250, 252, 0.15)" />
          </g>
          {/* Grand Carré de Pégase */}
          <path d="M 0,0 L 40,-15 L 55,25 L 10,40 Z" fill="rgba(248, 250, 252, 0.08)" stroke="rgba(254, 240, 138, 0.7)" strokeWidth="1.3" strokeDasharray="3 3" />
          <circle cx="0" cy="0" r="2.8" fill="#ffffff" />
          <circle cx="40" cy="-15" r="2.8" fill="#fca5a5" />
          <circle cx="55" cy="25" r="2.5" fill="#ffffff" />
          <circle cx="10" cy="40" r="2.8" fill="#ffffff" />
          <circle cx="-35" cy="24" r="2" fill="#ffffff" />
          <circle cx="-65" cy="34" r="2.5" fill="#fde047" />
          
          <text x="10" y="-85" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ PÉGASE ✦</text>
        </g>

        {/* 8. ORION (Le Grand Chasseur) */}
        <g transform={`translate(${cOrion.x}, ${cOrion.y}) scale(${cOrion.scale})`}>
          <g transform="translate(-25, -70)">
             <GiArcher size={140} color="rgba(191, 219, 254, 0.15)" />
          </g>
          {/* Lignes d'astérisme d'Orion */}
          <path d="M 10,-45 L 32,0 L 22,68 M 78,-35 L 54,-10 L 76,68 M 32,0 L 43,-5 L 54,-10 M 10,-45 L 46,-68 L 78,-35 M 22,68 L 76,68" fill="none" stroke="rgba(254, 240, 138, 0.75)" strokeWidth="1.3" strokeDasharray="3 3" />
          {/* Bételgeuse & Rigel */}
          <circle cx="10" cy="-45" r="10" fill="rgba(239, 68, 68, 0.3)" />
          <circle cx="10" cy="-45" r="4" fill="#f87171" />
          <circle cx="76" cy="68" r="10" fill="rgba(56, 189, 248, 0.3)" />
          <circle cx="76" cy="68" r="4" fill="#38bdf8" />
          {/* Autres étoiles */}
          <circle cx="78" cy="-35" r="2.8" fill="#bfdbfe" />
          <circle cx="22" cy="68" r="2.5" fill="#ffffff" />
          <circle cx="46" cy="-68" r="2" fill="#ffffff" />
          <circle cx="32" cy="0" r="2.4" fill="#ffffff" />
          <circle cx="43" cy="-5" r="2.4" fill="#ffffff" />
          <circle cx="54" cy="-10" r="2.4" fill="#ffffff" />
          
          <text x="45" y="-105" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ ORION ✦</text>
        </g>

        {/* 9. LA PETITE OURSE (Ursa Minor) */}
        <g transform={`translate(${cPetiteOurse.x}, ${cPetiteOurse.y}) scale(${cPetiteOurse.scale})`}>
          <g transform="translate(-5, -35)">
             <GiPolarBear size={80} color="rgba(248, 250, 252, 0.15)" />
          </g>
          {/* Lignes d'astérisme de la Petite Casserole */}
          <path d="M 0,0 L 16,12 L 32,6 L 46,20 L 68,14 L 62,-8 L 42,-4 L 46,20" fill="none" stroke="rgba(254, 240, 138, 0.75)" strokeWidth="1.2" strokeDasharray="3 3" />
          {/* Polaris */}
          <circle cx="0" cy="0" r="10" fill="rgba(254, 240, 138, 0.35)" />
          <circle cx="0" cy="0" r="4" fill="#ffffff" />
          <polygon points="0,-12 3,-3 12,0 3,3 0,12 -3,3 -12,0 -3,-3" fill="#fde047" />
          <polygon points="0,-7 2,-2 7,0 2,2 0,7 -2,2 -7,0 -2,-2" fill="#ffffff" transform="rotate(45)" />
          {/* Étoiles du bol */}
          <circle cx="16" cy="12" r="1.8" fill="#ffffff" />
          <circle cx="32" cy="6" r="1.8" fill="#ffffff" />
          <circle cx="46" cy="20" r="2" fill="#ffffff" />
          <circle cx="68" cy="14" r="2.6" fill="#fca5a5" />
          <circle cx="62" cy="-8" r="2.2" fill="#ffffff" />
          <circle cx="42" cy="-4" r="1.8" fill="#ffffff" />
          
          <text x="35" y="-85" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ PETITE OURSE & POLAIRE ✦</text>
        </g>

        {/* 10. LE CYGNE (Cygnus) */}
        <g transform={`translate(${cCygne.x}, ${cCygne.y}) scale(${cCygne.scale})`}>
          <g transform="translate(-25, -60)">
             <GiSwan size={140} color="rgba(248, 250, 252, 0.15)" />
          </g>
          {/* Lignes de la Croix du Nord */}
          <path d="M 45,-58 L 45,0 L 45,82 M -15,-38 L 18,-18 L 45,0 L 72,-18 L 105,-38" fill="none" stroke="rgba(254, 240, 138, 0.75)" strokeWidth="1.3" strokeDasharray="3 3" />
          {/* Deneb */}
          <circle cx="45" cy="-58" r="9" fill="rgba(255, 255, 255, 0.3)" />
          <circle cx="45" cy="-58" r="3.8" fill="#ffffff" />
          {/* Sadr */}
          <circle cx="45" cy="0" r="2.8" fill="#ffffff" />
          {/* Albiréo */}
          <circle cx="45" cy="82" r="3" fill="#fde047" />
          <circle cx="47" cy="84" r="1.5" fill="#38bdf8" />
          {/* Étoiles des Ailes */}
          <circle cx="18" cy="-18" r="2.2" fill="#ffffff" />
          <circle cx="-15" cy="-38" r="2" fill="#ffffff" />
          <circle cx="72" cy="-18" r="2.2" fill="#ffffff" />
          <circle cx="105" cy="-38" r="2" fill="#ffffff" />
          
          <text x="45" y="-85" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ LE CYGNE ✦</text>
        </g>

        {/* 11. LA LYRE (Lyra & Véga) */}
        <g transform={`translate(${cLyre.x}, ${cLyre.y}) scale(${cLyre.scale})`}>
          <g transform="translate(-5, -30)">
             <GiLyre size={80} color="rgba(191, 219, 254, 0.15)" />
          </g>
          {/* Lignes d'astérisme du parallélogramme */}
          <path d="M 0,0 L 22,-14 L 68,-8 L 45,20 L 0,0 M 45,20 L 68,32 L 68,-8 M 22,-14 L 0,0" fill="none" stroke="rgba(254, 240, 138, 0.75)" strokeWidth="1.2" strokeDasharray="3 3" />
          {/* Véga */}
          <circle cx="0" cy="0" r="10" fill="rgba(191, 219, 254, 0.35)" />
          <circle cx="0" cy="0" r="4" fill="#ffffff" />
          <polygon points="0,-12 3,-3 12,0 3,3 0,12 -3,3 -12,0 -3,-3" fill="#bfdbfe" />
          <polygon points="0,-7 2,-2 7,0 2,2 0,7 -2,2 -7,0 -2,-2" fill="#ffffff" transform="rotate(45)" />
          {/* Autres étoiles de la Lyre */}
          <circle cx="22" cy="-14" r="2" fill="#ffffff" />
          <circle cx="68" cy="-8" r="2.2" fill="#ffffff" />
          <circle cx="45" cy="20" r="2.2" fill="#ffffff" />
          <circle cx="68" cy="32" r="2.2" fill="#ffffff" />
          
          <text x="45" y="-70" fill="#f8fafc" fontSize="11" fontWeight="700" textAnchor="middle" letterSpacing="2.5">✦ LA LYRE & VÉGA ✦</text>
        </g>

      </g>
      {/* Twinkling background colored stars distributed organically without any alignment */}
      {backgroundStars.map((star) => {
        const cx = viewBoxLeft + star.u * viewBoxWidth;

        return (
          <circle
            key={`twinkle-star-${star.id}`}
            cx={cx}
            cy={star.cy}
            r={star.r}
            fill={star.color}
            opacity={star.isTwinkling ? undefined : star.baseOpacity}
            className={star.isTwinkling ? "animate-twinkle" : ""}
            style={star.isTwinkling ? { 
              '--base-opacity': star.baseOpacity,
              '--twinkle-dur': star.dur
            } as React.CSSProperties : undefined}
          />
        );
      })}

      {/* Earth Base silhouette (Sommet du Djurdjura) extending down to y=5600 across full width and into bottom screen */}
      <g transform="translate(0, 4220)">
        {/* Deep Mountain Mass spanning down through bottom screen */}
        <polygon
          points={`${viewBoxLeft - 300},1380 ${viewBoxLeft - 300},120 -80,130 100,20 200,90 300,10 480,130 ${viewBoxLeft + viewBoxWidth + 300},120 ${viewBoxLeft + viewBoxWidth + 300},1380`}
          fill="#0f172a"
        />
        <polygon
          points={`${viewBoxLeft - 300},1380 ${viewBoxLeft - 300},150 -40,140 150,50 200,110 350,40 440,140 ${viewBoxLeft + viewBoxWidth + 300},150 ${viewBoxLeft + viewBoxWidth + 300},1380`}
          fill="#020617"
          opacity="0.95"
        />
        {/* Layered mountain ridges descending down */}
        <polygon
          points={`${viewBoxLeft - 300},1380 ${viewBoxLeft - 300},320 60,260 200,340 380,240 ${viewBoxLeft + viewBoxWidth + 300},300 ${viewBoxLeft + viewBoxWidth + 300},1380`}
          fill="#090d1f"
          opacity="0.8"
        />
        <polygon
          points={`${viewBoxLeft - 300},1380 ${viewBoxLeft - 300},540 120,460 240,560 410,480 ${viewBoxLeft + viewBoxWidth + 300},520 ${viewBoxLeft + viewBoxWidth + 300},1380`}
          fill="#030712"
          opacity="0.9"
        />
        
        {/* Magical Berber Runes carved in mountain rocks at various elevations */}
        <g fill="#10b981" opacity="0.45" fontSize="14" fontWeight="bold">
          <text x="140" y="80" transform="rotate(-15 140 80)">ⵣ</text>
          <text x="240" y="70" transform="rotate(10 240 70)">ⵝ</text>
          <text x="280" y="50" transform="rotate(25 280 50)">ⵙ</text>
          <text x="100" y="240" transform="rotate(-8 100 240)" fontSize="18" fill="#38bdf8" opacity="0.35">ⵣ</text>
          <text x="310" y="270" transform="rotate(12 310 270)" fontSize="16" fill="#fde047" opacity="0.3">ⴼ</text>
          <text x="180" y="440" transform="rotate(5 180 440)" fontSize="22" fill="#10b981" opacity="0.25">ⵢ</text>
          <text x="290" y="520" transform="rotate(-10 290 520)" fontSize="18" fill="#38bdf8" opacity="0.25">ⵣ</text>
        </g>

        {/* Sorceress' Cauldron / Fire at the peak */}
        <ellipse cx="200" cy="90" rx="22" ry="8" fill="#000" stroke="#334155" strokeWidth="1.5" />
        
        {/* Magic Glowing Flame */}
        <path d="M 185 90 Q 200 40 215 90 Z" fill="url(#magicFire)" opacity="0.9">
          <animate attributeName="d" values="M 185 90 Q 200 40 215 90 Z; M 185 90 Q 200 20 215 90 Z; M 185 90 Q 200 40 215 90 Z" dur="2s" repeatCount="indefinite" />
        </path>
        
        {/* Magical Sparks */}
        {Array.from({ length: 8 }).map((_, i) => (
          <circle key={`spark-${i}`} cx="200" cy="80" r="1.5" fill="#a7f3d0">
            <animate attributeName="cy" values="80; -50" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
            <animate attributeName="cx" values={`200; ${180 + i * 5}`} dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="1; 0" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
          </circle>
        ))}
      </g>

      {/* ========================================================= */}
      {/* 2. FILS LUMINEUX (THE GLOWING LIGHT THREADS)              */}
      {/* ========================================================= */}

      {/* Draw constellation connections */}
      {segments.map((seg, idx) => (
        <g key={`thread-${idx}`}>
          {/* Main subtle starlight thread (inactive future path) */}
          <line
            x1={seg.x1} y1={seg.y1}
            x2={seg.x2} y2={seg.y2}
            stroke={seg.color}
            strokeWidth="1.5"
            strokeDasharray="4 6"
            strokeLinecap="round"
            opacity="0.3"
          />

          {/* Conquered radiant energy thread (up to current step) */}
          {seg.isPassed && (
            <>
              <line
                x1={seg.x1} y1={seg.y1}
                x2={seg.x2} y2={seg.y2}
                stroke={seg.color}
                strokeWidth="6"
                strokeLinecap="round"
                opacity="0.8"
                
              >
              </line>
              <line
                x1={seg.x1} y1={seg.y1}
                x2={seg.x2} y2={seg.y2}
                stroke="#ffffff"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.9"
              />
            </>
          )}
        </g>
      ))}

      {/* ========================================================= */}
      {/* 3. ÉTOILES DU CHEMIN (THE 51 STAR NODES)                  */}
      {/* ========================================================= */}
      {nodes.map((node, idx) => {
        const isCurrent = idx === currentStep;
        const isPassed = idx < currentStep;
        const isMilestone = idx % 10 === 0 && idx > 0 && idx < 50;

        if (idx === 0 || idx === 50) return null;

        return (
          <g key={`star-node-${idx}`} transform={`translate(${node.x}, ${node.y})`}>
            {/* Ambient Star Halo */}
            {isCurrent && (
              <g>
                <circle cx="0" cy="0" r="24" fill={node.color || "#38bdf8"} opacity="0.3">
                  <animate attributeName="opacity" values="0.2;0.5;0.2" dur="2s" repeatCount="indefinite" />
                </circle>
                <circle cx="0" cy="0" r="14" fill="#ffffff" opacity="0.4"  />
              </g>
            )}

            {isPassed && (
              <circle cx="0" cy="0" r="8" fill={node.color || "#10b981"} opacity="0.3"  />
            )}

            {/* Geometric Glowing Star */}
            {isCurrent ? (
              <g >
                {/* 8-point bright star matching constellation color */}
                <polygon points="0,-12 3,-3 12,0 3,3 0,12 -3,3 -12,0 -3,-3" fill={node.color || "#fde047"} />
                <polygon points="0,-8 2,-2 8,0 2,2 0,8 -2,2 -8,0 -2,-2" fill="#ffffff" transform="rotate(45)" />
                <circle cx="0" cy="0" r="3" fill="#ffffff" />
              </g>
            ) : isPassed ? (
              <g>
                {/* Conquered Star in constellation color */}
                <polygon points="0,-8 2,-2 8,0 2,2 0,8 -2,2 -8,0 -2,-2" fill={node.color || "#34d399"} />
                <circle cx="0" cy="0" r="2" fill="#ffffff" />
              </g>
            ) : isMilestone ? (
              <g>
                {/* Major Stellar Milestone */}
                <polygon points="0,-9 2.5,-2.5 9,0 2.5,2.5 0,9 -2.5,2.5 -9,0 -2.5,-2.5" fill={node.color || "#facc15"} opacity="0.9" />
                <circle cx="0" cy="0" r="2" fill="#ffffff" />
              </g>
            ) : (
              <g opacity="0.75">
                {/* Unreached Twinkling Star Point */}
                <polygon points="0,-5 1.5,-1.5 5,0 1.5,1.5 0,5 -1.5,1.5 -5,0 -1.5,-1.5" fill={node.color || "#7dd3fc"} opacity="0.6" />
                <circle cx="0" cy="0" r="1.2" fill="#ffffff" />
              </g>
            )}
          </g>
        );
      })}

      {/* ========================================================= */}
      {/* 4. LE SANCTUAIRE D'AYYUR (LA LUNE ÉPURÉE)                 */}
      {/* ========================================================= */}
      <g transform="translate(200, 160)">
        {/* Auroral Moon Aura (without rays) */}
        <circle
          cx="0"
          cy="0"
          r="140"
          fill="url(#moonGlow)"
          opacity={0.45 + (moonBrightness / 100) * 0.55}
        />

        {/* The Moon Orb (Ayyur) */}
        <circle
          cx="0"
          cy="0"
          r="54"
          fill="#fef08a"
          stroke="#eab308"
          strokeWidth="2.5"
          
        />

        {/* Minimalist Moon relief & Yaz Symbol */}
        <circle cx="-18" cy="-14" r="10" fill="#eab308" opacity="0.3" />
        <circle cx="20" cy="-10" r="12" fill="#eab308" opacity="0.25" />
        <circle cx="-6" cy="18" r="14" fill="#eab308" opacity="0.3" />

        <text
          x="0"
          y="15"
          fill="#78350f"
          fontSize="42"
          fontWeight="900"
          textAnchor="middle"
          
        >
          ⵣ
        </text>
      </g>
    </svg>
  );
});
